// ============================================================
// Craft a Brew — BJCP Attribution Hide Module v1.0
// Date: April 14, 2026
// Load AFTER recipe-builder.js AND recipe-builder-enhancements.js.
//
// PURPOSE
// -------
// This is a self-contained module that hides all BJCP-branded text
// from the Recipe Builder UI. It exists because we use BJCP-derived
// style ranges in the calculator engine but do not yet have explicit
// permission from the BJCP to display their branding or category
// numbers (e.g. "21A", "BJCP 18B").
//
// The underlying calculator code in recipe-builder.js is UNCHANGED.
// All BJCP_STYLES data, the matchStyle() function, the scoring logic,
// and the BJCP IDs as data keys remain intact. This module only
// rewrites display strings AFTER the calculator has rendered them.
//
// WHAT IT HIDES / REWRITES
// ------------------------
// 1. Style dropdown options:
//      "18A - Blonde Ale"  →  "Blonde Ale"
//      "21A - American IPA" →  "American IPA"
//    Strips the leading category code and " - " separator.
//
// 2. Style match label in the Recipe Stats panel:
//      "BEST BJCP STYLE MATCH" → "BEST STYLE MATCH"
//    Rewrites the .style-match-label element's text.
//
// 3. Style ID line under the matched style name:
//      "BJCP 18B" (the small line under "American Pale Ale")
//    Hidden entirely via CSS display:none.
//
// 4. Any other DOM text node containing the literal string "BJCP"
//    is rewritten with that token removed. Belt-and-suspenders catch
//    for anything I might have missed.
//
// HOW TO REMOVE LATER (when BJCP approval is granted)
// ----------------------------------------------------
// 1. Delete this file from the Shopify theme assets folder
// 2. Remove the corresponding {{ 'recipe-builder-bjcp-hide.js' | asset_url | script_tag }}
//    line from recipe-builder-block.liquid (or wherever this file is
//    included)
// That's it. No other files need editing — recipe-builder.js,
// recipe-builder-enhancements.js, and recipe-builder-redesign.css
// are all untouched by the install of this module, so removing it
// returns the calculator to its full BJCP-attributed state.
//
// IMPLEMENTATION NOTES
// --------------------
// - Uses MutationObserver to catch the case where matchStyle() updates
//   the style match label after our initial pass. Without it, every
//   recalculate() would re-render the BJCP text and we'd see it flash.
// - The observer is scoped to specific elements rather than document.body
//   to avoid the performance hit of watching the whole DOM.
// - Dropdown rewriting wraps populateStyleSelect-equivalent behavior:
//   we rewrite at init AND any time options are mutated.
// - Idempotent: rewriting an already-rewritten label is a no-op so
//   running multiple times is safe.
// ============================================================

(function () {
  'use strict';

  // ------------------------------------------------------------
  // Wait for the calculator to finish initializing
  // ------------------------------------------------------------
  function ready(fn) {
    if (window.PRODUCTS && typeof window.recalculate === 'function') {
      fn();
    } else {
      setTimeout(function () { ready(fn); }, 100);
    }
  }

  ready(init);

  function init() {
    try {
      injectCss();
      rewriteStyleDropdown();
      rewriteStyleMatchLabel();
      rewriteAllBjcpText();
      attachObservers();
      console.log('[CAB] BJCP hide module v1.0 loaded.');
    } catch (e) {
      console.error('[CAB] BJCP hide module failed to initialize:', e);
    }
  }

  // ------------------------------------------------------------
  // 1. Inject CSS to hide static BJCP elements
  // ------------------------------------------------------------
  // The .style-match-id element renders the "BJCP 18B" line under
  // the matched style name. Hiding it via CSS is cleaner than
  // emptying its text in JS because there's no flash on first paint
  // and no need to fight the calculator's recalculate() rewriting.
  // ------------------------------------------------------------
  function injectCss() {
    var css = [
      '.cab-rb .style-match-id {',
      '  display: none !important;',
      '}'
    ].join('\n');

    var style = document.createElement('style');
    style.id = 'cab-bjcp-hide-styles';
    style.textContent = css;
    document.head.appendChild(style);
  }

  // ------------------------------------------------------------
  // 2. Rewrite style dropdown option labels
  // ------------------------------------------------------------
  // Native <select> options are rebuilt by the search-select wrapper
  // when it lazily renders the dropdown panel. We rewrite both the
  // underlying <option> elements (source of truth) AND any rendered
  // .search-select-opt elements that may already be in the DOM.
  //
  // Pattern matches:
  //   "18A - Blonde Ale" → "Blonde Ale"
  //   "21B - NEIPA"      → "NEIPA"
  //   "26C - Belgian Tripel" → "Belgian Tripel"
  // Regex: leading 1-2 digits + 1 letter + " - "
  // ------------------------------------------------------------
  var BJCP_OPTION_RE = /^\d{1,2}[A-Z]\s*-\s*/;

  function stripBjcpPrefix(text) {
    if (!text) return text;
    return text.replace(BJCP_OPTION_RE, '');
  }

  function rewriteStyleDropdown() {
    var sel = document.getElementById('styleSelect');
    if (!sel) return;

    // Rewrite the underlying <option> elements
    Array.prototype.slice.call(sel.options).forEach(function (opt) {
      // Skip the "Calculate Best Match" auto option
      if (opt.value === 'auto') return;
      var stripped = stripBjcpPrefix(opt.textContent);
      if (stripped !== opt.textContent) {
        opt.textContent = stripped;
      }
    });

    // Also rewrite any rendered .search-select-opt elements inside
    // the dropdown wrapper, in case the wrapper has already built
    // them. The text content of the rendered options is copied from
    // the <option> elements at build time, so they need a separate
    // pass.
    var wrap = sel.closest('.search-select-wrap');
    if (wrap) {
      wrap.querySelectorAll('.search-select-opt').forEach(function (rendered) {
        var stripped = stripBjcpPrefix(rendered.textContent);
        if (stripped !== rendered.textContent) {
          rendered.textContent = stripped;
        }
      });

      // Also rewrite the visible display input if it currently shows
      // a BJCP-prefixed label
      var displayInput = wrap.querySelector('.search-select-input');
      if (displayInput) {
        var strippedDisplay = stripBjcpPrefix(displayInput.value);
        if (strippedDisplay !== displayInput.value) {
          displayInput.value = strippedDisplay;
        }
      }
    }
  }

  // ------------------------------------------------------------
  // 3. Rewrite the "BEST BJCP STYLE MATCH" label
  // ------------------------------------------------------------
  // The .style-match-label element renders this header above the
  // matched style name. Rewrite its text content to remove "BJCP".
  // Idempotent — rewriting an already-clean label is a no-op.
  // ------------------------------------------------------------
  function rewriteStyleMatchLabel() {
    document.querySelectorAll('.cab-rb .style-match-label').forEach(function (el) {
      var txt = el.textContent || '';
      if (txt.indexOf('BJCP') >= 0) {
        // "BEST BJCP STYLE MATCH" → "BEST STYLE MATCH"
        // Also handles any whitespace cleanup from the removed token.
        el.textContent = txt.replace(/\s*BJCP\s*/g, ' ').replace(/\s+/g, ' ').trim();
      }
    });
  }

  // ------------------------------------------------------------
  // 4. Belt-and-suspenders: scan all text nodes in the recipe
  //    builder area for the literal "BJCP" and strip it
  // ------------------------------------------------------------
  // This catches anything the targeted rewrites above may have
  // missed. Limited to leaf text nodes inside .cab-rb so we don't
  // accidentally rewrite other parts of the page (theme header,
  // footer, etc.) that may legitimately contain "BJCP".
  // ------------------------------------------------------------
  function rewriteAllBjcpText() {
    var root = document.querySelector('.cab-rb');
    if (!root) return;

    // Use a TreeWalker to find all text nodes containing "BJCP"
    var walker = document.createTreeWalker(
      root,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: function (node) {
          // Skip text inside <script> and <style> tags
          var parent = node.parentNode;
          if (!parent) return NodeFilter.FILTER_REJECT;
          var tag = parent.tagName;
          if (tag === 'SCRIPT' || tag === 'STYLE') return NodeFilter.FILTER_REJECT;
          // Only accept nodes that contain "BJCP"
          return (node.nodeValue && node.nodeValue.indexOf('BJCP') >= 0)
            ? NodeFilter.FILTER_ACCEPT
            : NodeFilter.FILTER_REJECT;
        }
      },
      false
    );

    var nodesToFix = [];
    var node;
    while ((node = walker.nextNode())) {
      nodesToFix.push(node);
    }

    nodesToFix.forEach(function (textNode) {
      // Remove "BJCP" and clean up extra spaces.
      // "BJCP 18B"          → "18B" → caught by separate logic below
      // "BEST BJCP STYLE"   → "BEST STYLE"
      // "the BJCP standard" → "the standard"
      var cleaned = textNode.nodeValue
        .replace(/\bBJCP\s+/g, '')
        .replace(/\s+BJCP\b/g, '')
        .replace(/\bBJCP\b/g, '')
        .replace(/\s+/g, ' ');
      // Then strip any remaining bare category code that's left
      // dangling (e.g. "18B" alone after "BJCP" was removed)
      cleaned = cleaned.replace(/\b\d{1,2}[A-Z]\b/g, '').replace(/\s+/g, ' ').trim();
      if (cleaned !== textNode.nodeValue) {
        textNode.nodeValue = cleaned;
      }
    });
  }

  // ------------------------------------------------------------
  // 5. MutationObservers to catch dynamic re-renders
  // ------------------------------------------------------------
  // The calculator's matchStyle() function rewrites .style-match-label
  // and .style-match-id on every recalculate() (which fires on every
  // ingredient change). Without an observer, our initial rewrite
  // would be clobbered the first time the user added a fermentable.
  //
  // We attach two scoped observers:
  //   - One on the style match panel, to catch label re-renders
  //   - One on the style select wrapper, to catch dropdown rebuilds
  // Both are scoped to specific elements (not document.body) for
  // performance.
  // ------------------------------------------------------------
  function attachObservers() {
    // Observer for the style match panel — re-runs label rewrite
    // whenever matchStyle() updates the heading text
    var matchPanel = document.querySelector('.cab-rb .style-match');
    if (matchPanel) {
      try {
        var matchObs = new MutationObserver(function () {
          rewriteStyleMatchLabel();
          rewriteAllBjcpText();
        });
        matchObs.observe(matchPanel, {
          childList: true,
          characterData: true,
          subtree: true
        });
      } catch (e) {
        console.warn('[CAB] BJCP hide: match panel observer failed:', e);
      }
    }

    // Observer for the style select element — re-runs dropdown
    // rewrite if the option list changes
    var styleSel = document.getElementById('styleSelect');
    if (styleSel) {
      try {
        var selObs = new MutationObserver(function () {
          rewriteStyleDropdown();
        });
        selObs.observe(styleSel, {
          childList: true,
          subtree: true,
          characterData: true
        });
      } catch (e) {
        console.warn('[CAB] BJCP hide: style select observer failed:', e);
      }
    }

    // Also re-run dropdown rewrite once the search-select wrapper
    // has had time to lazily render its options (the user clicking
    // the dropdown for the first time builds the .search-select-opt
    // children, which need to be rewritten too)
    var wrap = styleSel ? styleSel.closest('.search-select-wrap') : null;
    if (wrap) {
      try {
        var wrapObs = new MutationObserver(function () {
          rewriteStyleDropdown();
        });
        wrapObs.observe(wrap, {
          childList: true,
          subtree: true
        });
      } catch (e) {
        console.warn('[CAB] BJCP hide: wrap observer failed:', e);
      }
    }
  }
})();